import { useMemo } from "react";
import { TARGET_IDS } from "@/shared/sphinx";
import { useHostsList, useHostDetail } from "@/data/targets";
import type { HostConfigDtoV2 } from "@/lib/client-config-v2";
import { shouldQueryProjectId } from "./useProjects";
import { withoutPrivateScenarioBackingHosts } from "@/lib/host-owner-scope";

/**
 * Product ownership of a host. Defined in `@/lib/host-owner-scope` alongside
 * the "hide private scenario-backing clients" predicate — a pure module, so
 * surfaces that mock this hooks module in tests still get the real rule.
 * Re-exported here because host consumers reach for it beside `HostListItem`.
 */
export type { HostOwnerScope } from "@/lib/host-owner-scope";
import type { HostOwnerScope } from "@/lib/host-owner-scope";

export interface HostListItem {
  hostId: string;
  name: string;
  hostConfigId: string;
  modelId: string;
  serverCount: number;
  // Additive (PR: standalone hosts). Older backends omit these; readers must
  // treat absent as null/false rather than assume presence.
  ownerScope?: HostOwnerScope;
  hasComputer?: boolean;
  createdAt: number;
  updatedAt: number;
  // sphinx: target matrix fields (see data/targets.ts).
  enabled?: boolean;
  model?: string | null;
  /** Every model this client runs (migration 012); empty = fall back to `model`. */
  models?: string[];
  serverIds?: string[];
}

export interface HostDetail {
  hostId: string;
  name: string;
  config: HostConfigDtoV2;
  // Additive: the Scenario surface reads this to decide whether to render the
  // "managed by Swarms, no publish surface" notice instead of back-minting.
  ownerScope?: HostOwnerScope;
}

/**
 * The project's clients, with private scenario-backing ones REMOVED by default.
 *
 * The filter lives here rather than at each call site because there are ~18 of
 * them and the ones that matter are leaves: `HostPicker`, `MultiHostPicker`,
 * `ClientAttachmentsEditor` and friends each call this hook themselves, so a
 * caller that filters the list it holds does nothing about the picker rendered
 * inside it. Defaulting to the safe set is the only version of this rule that
 * actually holds — a new picker gets it without knowing it exists.
 *
 * `includePrivateBacking` is for the handful of consumers doing an id → name
 * or id → row LOOKUP rather than offering a choice; for them a missing host is
 * a rendering bug, not a safety win. Pass it deliberately and say why.
 */
export function useHostList({
  isAuthenticated,
  projectId,
  includePrivateBacking = false,
}: {
  isAuthenticated: boolean;
  projectId: string | null;
  includePrivateBacking?: boolean;
}): {
  hosts: HostListItem[];
  isLoading: boolean;
} {
  // Skip until `projectId` is a real Convex id. A transient LOCAL project id
  // (UUID, or a `local_`/`project_` placeholder) flows in while the shared
  // Convex id is still resolving — passing it to a `v.id("projects")` query
  // throws an ArgumentValidationError. `shouldQueryProjectId` is the same guard
  // every other project-scoped Convex query uses.
  const result = useHostsList(
    isAuthenticated && shouldQueryProjectId(projectId)
      ? ({ projectId } as any)
      : "skip",
  ) as HostListItem[] | null | undefined;

  const hosts = useMemo(() => {
    const all = result ?? [];
    return includePrivateBacking
      ? all
      : withoutPrivateScenarioBackingHosts(all);
  }, [result, includePrivateBacking]);

  return {
    hosts,
    isLoading: result === undefined,
  };
}

/**
 * Whether `hostId` can resolve to a host at all. In sphinx the hosts are the
 * fixed target matrix (`TARGET_IDS`), so anything else — a catalog slug typed
 * into `/clients/:hostId`, a stale persisted id — is rejected without a
 * round-trip, and `HostsRoute` uses the same check to keep such a value OUT of
 * persisted state.
 */
export function shouldQueryHostId(hostId: string | null | undefined): boolean {
  const normalized = hostId?.trim();
  return Boolean(
    normalized && (TARGET_IDS as readonly string[]).includes(normalized),
  );
}

export function useHost({
  isAuthenticated,
  hostId,
}: {
  isAuthenticated: boolean;
  hostId: string | null;
}): {
  host: HostDetail | null;
  isLoading: boolean;
} {
  const shouldQuery = isAuthenticated && shouldQueryHostId(hostId);
  // Trimmed, so a padded id can't pass the guard and then fail validation.
  const queryHostId = hostId?.trim() ?? "";

  const result = useHostDetail(
    shouldQuery ? ({ hostId: queryHostId } as any) : "skip",
  ) as HostDetail | null | undefined;

  return {
    host: result ?? null,
    // A skipped query never resolves, so reporting it as loading would leave
    // callers (the Connect canvas, the compare grid) spinning forever.
    isLoading: shouldQuery && result === undefined,
  };
}

export { useHostMutations } from "@/data/targets";
